// Axios client + auth context
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

let _token = null;
let _onUnauthorized = null;

// ============ P3 — /api/me sessionStorage stale-while-revalidate ============
// Goal: cut the ~660 ms blocking /api/me round-trip on every full-page
// reload / deep-link / new-tab visit. Strategy: keep the last successful
// /api/me payload in sessionStorage; on AuthProvider mount, render the app
// immediately from cache (if fresh enough) AND fire /api/me in the background
// to revalidate. The backend remains the source of truth — if the network
// call reports a different user/company/admin state, we update React state
// silently.
//
// Storage: sessionStorage ONLY (cleared when the tab closes — never spans
// sessions). TTL: 5 minutes. Schema validation guards against malformed
// snapshots (older app versions, partial writes).
const _ME_CACHE_KEY = "ordly_me_swr";
const _ME_CACHE_TTL_MS = 5 * 60 * 1000;

function _readMeCache() {
    try {
        const raw = sessionStorage.getItem(_ME_CACHE_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj || typeof obj !== "object") return null;
        if (typeof obj.cached_at !== "number") return null;
        if (Date.now() - obj.cached_at > _ME_CACHE_TTL_MS) return null;
        const d = obj.data;
        // Validate the shape we depend on. Reject silently if anything is
        // missing/malformed so we never serve broken state.
        if (!d || typeof d !== "object") return null;
        if (!d.user || typeof d.user !== "object") return null;
        if (typeof d.user.id !== "string" || typeof d.user.email !== "string") return null;
        if (typeof d.user.role !== "string") return null;
        if (!d.company || typeof d.company !== "object") return null;
        if (typeof d.company.id !== "string") return null;
        if (typeof d.is_platform_admin !== "boolean") return null;
        return d;
    } catch {
        return null;
    }
}

function _writeMeCache(data) {
    try {
        // Only cache the canonical shape; ignore extras to keep the snapshot
        // small and predictable.
        sessionStorage.setItem(
            _ME_CACHE_KEY,
            JSON.stringify({ cached_at: Date.now(), data }),
        );
    } catch {
        // sessionStorage may be unavailable (private mode / quota); fail
        // silently — we just lose the SWR optimisation.
    }
}

function _clearMeCache() {
    try { sessionStorage.removeItem(_ME_CACHE_KEY); } catch { /* ignore */ }
}

function _meStateDiffers(a, b) {
    // Compare the fields the UI actually reads. Returns true if state must
    // be re-set from the network response.
    if (!a || !b) return true;
    if (a.user?.id !== b.user?.id) return true;
    if (a.user?.email !== b.user?.email) return true;
    if (a.user?.role !== b.user?.role) return true;
    if (a.user?.language !== b.user?.language) return true;
    if (a.company?.id !== b.company?.id) return true;
    if (a.company?.name !== b.company?.name) return true;
    if (!!a.is_platform_admin !== !!b.is_platform_admin) return true;
    return false;
}

export function setToken(token) {
    _token = token;
    if (token) {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
            localStorage.setItem("ordly_token", token);
        } catch {
            // localStorage may fail in private mode; safe to ignore
        }
    } else {
        delete api.defaults.headers.common["Authorization"];
        try {
            localStorage.removeItem("ordly_token");
        } catch {
            // ignore
        }
        // Token cleared → cached /me must go too. Covers both explicit logout
        // and a 401 interceptor wipe.
        _clearMeCache();
    }
}

api.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            setToken(null);
            if (_onUnauthorized) _onUnauthorized();
        }
        return Promise.reject(err);
    }
);

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/me");
            setUser(data.user);
            setCompany(data.company);
            setIsPlatformAdmin(!!data.is_platform_admin);
            // P3 — persist the canonical /me snapshot for the next reload's
            // stale-while-revalidate. Always overwrites; backend is truth.
            _writeMeCache(data);
            // Phase 7 Batch E — let i18n adopt backend language when the user
            // has not made an explicit choice (no `ordly_lang` in localStorage).
            try {
                window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                    detail: { language: data.user?.language },
                }));
            } catch {
                // dispatch failure is non-blocking
            }
            return data;
        } catch {
            setUser(null);
            setCompany(null);
            setIsPlatformAdmin(false);
            _clearMeCache();
            return null;
        }
    }, []);

    const bootstrap = useCallback(async () => {
        setLoading(true);
        try {
            const saved = localStorage.getItem("ordly_token");
            if (!saved) return;
            setToken(saved);

            // P3 — stale-while-revalidate. If a fresh sessionStorage snapshot
            // exists, render the app IMMEDIATELY from cache (unblocks routes
            // ~660 ms earlier on cold reloads). Then fire /api/me in the
            // background to revalidate and silently fix state if anything
            // changed since the snapshot was written. If the cached payload
            // is missing/malformed/stale, this branch is skipped and we fall
            // back to the original "await /me, then render" path — same UX
            // as today.
            const cached = _readMeCache();
            let cachedSnapshot = null;
            if (cached) {
                setUser(cached.user);
                setCompany(cached.company);
                setIsPlatformAdmin(!!cached.is_platform_admin);
                try {
                    window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                        detail: { language: cached.user?.language },
                    }));
                } catch { /* non-blocking */ }
                cachedSnapshot = cached;
                // Routes can render now. The background /me below will
                // correct state silently if needed.
                setLoading(false);
            }

            try {
                const { data } = await api.get("/me");
                // Always write the freshest snapshot back.
                _writeMeCache(data);
                if (!cachedSnapshot || _meStateDiffers(cachedSnapshot, data)) {
                    setUser(data.user);
                    setCompany(data.company);
                    setIsPlatformAdmin(!!data.is_platform_admin);
                    try {
                        window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                            detail: { language: data.user?.language },
                        }));
                    } catch { /* non-blocking */ }
                }
            } catch {
                // Network or 401. If we had a cached snapshot we already
                // rendered, the 401 interceptor will wipe state. If we had no
                // cache, clear local state explicitly.
                if (!cachedSnapshot) {
                    setUser(null);
                    setCompany(null);
                    setIsPlatformAdmin(false);
                    _clearMeCache();
                }
            }
        } catch {
            // ignore bootstrap errors; user simply unauthenticated
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        _onUnauthorized = () => {
            setUser(null);
            setCompany(null);
            setIsPlatformAdmin(false);
        };
        let cancelled = false;
        (async () => {
            if (!cancelled) await bootstrap();
        })();
        return () => {
            cancelled = true;
            _onUnauthorized = null;
        };
    }, [bootstrap]);

    const login = useCallback(async (email, password, remember_device = false) => {
        const { data } = await api.post("/auth/login", { email, password, remember_device });
        setToken(data.token);
        setUser(data.user);
        setCompany(data.company);
        setIsPlatformAdmin(!!data.is_platform_admin);
        // P3 — seed the SWR cache so the next reload boots instantly.
        _writeMeCache({ user: data.user, company: data.company, is_platform_admin: !!data.is_platform_admin });
        try {
            window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                detail: { language: data.user?.language },
            }));
        } catch { /* ignore */ }
        return data;
    }, []);

    const register = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        // Phase 11 — registration no longer returns a session token. It
        // creates the user in pending state and starts the 6-digit email
        // verification challenge. Caller routes to /verify-email.
        if (data?.status === "verification_required") {
            return data;  // { status, email, message_pt, message_en, ... }
        }
        // Legacy fallback (shouldn't happen post-Phase-11, but keeps us safe
        // if the backend is ever downgraded).
        setToken(data.token);
        setUser(data.user);
        setCompany(data.company);
        setIsPlatformAdmin(!!data.is_platform_admin);
        try {
            window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                detail: { language: data.user?.language },
            }));
        } catch { /* ignore */ }
        return data;
    }, []);

    const verifyEmail = useCallback(async (email, code) => {
        const { data } = await api.post("/auth/verify-email", { email, code });
        setToken(data.token);
        setUser(data.user);
        setCompany(data.company);
        setIsPlatformAdmin(!!data.is_platform_admin);
        _writeMeCache({ user: data.user, company: data.company, is_platform_admin: !!data.is_platform_admin });
        try {
            window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                detail: { language: data.user?.language },
            }));
        } catch { /* ignore */ }
        return data;
    }, []);

    const resendVerification = useCallback(async (email) => {
        const { data } = await api.post("/auth/resend-verification", { email });
        return data;
    }, []);

    // Phase 12 — password recovery. Both endpoints are enumeration-safe,
    // so we don't try to interpret the response shape here — the page
    // component decides what to render based on which step it's on.
    const forgotPassword = useCallback(async (email) => {
        const { data } = await api.post("/auth/forgot-password", { email });
        return data;
    }, []);

    const resetPassword = useCallback(async (email, code, newPassword) => {
        const { data } = await api.post("/auth/reset-password", {
            email,
            code,
            new_password: newPassword,
        });
        return data;
    }, []);

    const requestMagicLink = useCallback(async (email) => {
        const { data } = await api.post("/auth/magic-link/request", { email });
        return data;
    }, []);

    const consumeMagicLink = useCallback(async (token) => {
        const { data } = await api.post("/auth/magic-link/consume", { token });
        setToken(data.token);
        setUser(data.user);
        setCompany(data.company);
        setIsPlatformAdmin(!!data.is_platform_admin);
        _writeMeCache({ user: data.user, company: data.company, is_platform_admin: !!data.is_platform_admin });
        try {
            window.dispatchEvent(new CustomEvent("ordly:lang-from-backend", {
                detail: { language: data.user?.language },
            }));
        } catch { /* ignore */ }
        return data;
    }, []);

    const logout = useCallback(() => {
        setToken(null);     // also wipes _ME_CACHE via _clearMeCache()
        setUser(null);
        setCompany(null);
        setIsPlatformAdmin(false);
    }, []);

    return (
        <AuthCtx.Provider
            value={{ user, company, isPlatformAdmin, loading, login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout, refresh, requestMagicLink, consumeMagicLink }}
        >
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}
